class ParallaxImages {
    constructor() {
        this.images = [];
        this.imageData = new Map();
        this.isInitialized = false;
        this.ticking = false;
        this.lastScrollTop = 0;
        this.scrollThreshold = 3;
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        this.isLowEndDevice = this.detectLowEndDevice();
        this.frameSkip = this.isLowEndDevice ? 2 : 1;
        this.frameCount = 0;
        
        this.observer = null;
        this.visibleImages = new Set();
        
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
            this.init();
        }
    }
    
    detectLowEndDevice() {
        const cores = navigator.hardwareConcurrency || 2;
        const memory = navigator.deviceMemory || 2;
        return cores <= 2 || memory <= 2;
    }

    init() {
        if (this.isInitialized) return;
        
        if (!window.requestAnimationFrame) {
            return;
        }
        
        this.findParallaxImages();
        
        if (this.images.length === 0) return;
        this.setupScrollListener();
        
        this.initializeImageScales();
        
        this.isInitialized = true;
    }

    findParallaxImages() {
        const parallaxImages = document.querySelectorAll('.parallax-image');
        const containerImages = document.querySelectorAll('.parallax-container img');
        
        const allImages = [...parallaxImages, ...containerImages];
        this.images = [...new Set(allImages)];
        
        this.setupIntersectionObserver();
        
        containerImages.forEach(img => {
            if (!img.classList.contains('parallax-image')) {
                img.classList.add('parallax-image');
            }
            
            this.cacheImageData(img);
        });
        
        parallaxImages.forEach(img => {
            this.cacheImageData(img);
        });
    }
    
    setupIntersectionObserver() {
        if (!window.IntersectionObserver) return;
        
        const options = {
            root: null,
            rootMargin: '50px 0px',
            threshold: 0
        };
        
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.visibleImages.add(entry.target);
                } else {
                    this.visibleImages.delete(entry.target);
                }
            });
        }, options);
        
        this.images.forEach(img => {
            this.observer.observe(img);
        });
    }
    
    cacheImageData(img) {
        const rect = img.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        this.imageData.set(img, {
            offsetTop: rect.top + scrollTop,
            height: rect.height,
            lastScale: null,
            needsUpdate: true
        });
    }
    
    initializeImageScales() {
        const windowHeight = window.innerHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        this.images.forEach(img => {
            this.updateImageScale(img, windowHeight, scrollTop, true);
        });
    }

    setupScrollListener() {
        window.addEventListener('scroll', () => {
            this.requestTick();
        }, { passive: true });
        
        window.addEventListener('resize', () => {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => {
                this.requestTick();
            }, 150);
        }, { passive: true });
    }

    requestTick() {
        if (!this.ticking) {
            requestAnimationFrame(() => {
                this.updateAllImages();
                this.ticking = false;
            });
            this.ticking = true;
        }
    }

    updateAllImages() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        if (Math.abs(scrollTop - this.lastScrollTop) < this.scrollThreshold) {
            return;
        }
        
        this.frameCount++;
        if (this.frameCount % this.frameSkip !== 0) {
            return;
        }
        
        this.lastScrollTop = scrollTop;
        const windowHeight = window.innerHeight;
        
        const imagesToUpdate = this.observer ? 
            Array.from(this.visibleImages) : 
            this.images;
        
        imagesToUpdate.forEach(img => {
            this.updateImageScale(img, windowHeight, scrollTop, false);
        });
    }

    updateImageScale(img, windowHeight, scrollTop, isInitial = false) {
        const cachedData = this.imageData.get(img);
        if (!cachedData) return;
        
        const imageTop = cachedData.offsetTop - scrollTop;
        const imageBottom = imageTop + cachedData.height;
        
        if (!isInitial && (imageTop > windowHeight + 100 || imageBottom < -100)) {
            return;
        }
        
        let progress;
        
        if (imageTop >= windowHeight) {
            progress = 0;
        } else if (imageBottom <= 0) {
            progress = 1;
        } else {
            const totalDistance = windowHeight + cachedData.height;
            const traveledDistance = windowHeight - imageTop;
            progress = traveledDistance / totalDistance;
        }
        
        progress = Math.max(0, Math.min(1, progress));
        
        const isShowcaseZoomImage = img.classList.contains('product-showcase__image--zoomable');

        if (!isInitial && this.isMobile && !this.isLowEndDevice && !isShowcaseZoomImage) {
            progress = this.easeOutCubic(progress);
        }
        
        const scale = 1.2 - (0.2 * progress);
        const roundedScale = Math.round(scale * 1000) / 1000;
        
        if (!isInitial && cachedData.lastScale && Math.abs(cachedData.lastScale - roundedScale) < 0.001) {
            return;
        }
        
        cachedData.lastScale = roundedScale;
        
        this.applyTransform(img, roundedScale, isInitial);
    }
    
    applyTransform(img, scale, isInitial = false) {
        if (this.isMobile || this.isLowEndDevice) {
            img.style.transform = `scale3d(${scale}, ${scale}, 1)`;
            img.style.webkitTransform = `scale3d(${scale}, ${scale}, 1)`;
        } else {
            img.style.transform = `scale(${scale})`;
        }
    }
    
    easeOutCubic(t) {
        return 1 - Math.pow(1 - t, 3);
    }

    addImage(img) {
        if (!img || this.images.includes(img)) return;
        
        img.classList.add('parallax-image');
        this.images.push(img);
        
        this.cacheImageData(img);
        
        if (this.observer) {
            this.observer.observe(img);
        }
        
        if (this.isInitialized) {
            this.requestTick();
        }
    }

    refresh() {
        this.destroy();
        this.images = [];
        this.imageData.clear();
        this.visibleImages.clear();
        this.isInitialized = false;
        this.init();
    }

    destroy() {
        window.removeEventListener('scroll', this.requestTick);
        window.removeEventListener('resize', this.requestTick);
        
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        
        this.images = [];
        this.imageData.clear();
        this.visibleImages.clear();
        this.isInitialized = false;
    }
    
    recalculateCache() {
        this.images.forEach(img => {
            this.cacheImageData(img);
        });
    }
}

window.ParallaxImages = new ParallaxImages();

window.addParallaxImage = (img) => window.ParallaxImages.addImage(img);
window.refreshParallaxImages = () => window.ParallaxImages.refresh();
